/**
 * Scheduling slice of the English catalog — bookings, the week calendar, and
 * the round-robin rota.
 *
 * Carved out for the reason enMembers.ts records: en.ts sits against the
 * 500-line fileSize ceiling and is the repo's recorded merge hotspot, so every
 * parallel branch appends to the same tail. The rota keys were the twelve lines
 * that pushed it to 501L and turned the ratchet red — the second time that
 * exact number has appeared — so the whole `bookings.*` vocabulary moves here
 * alongside them rather than just the overflow.
 *
 * Spread back into `en` in en.ts, so `t()` and MessageKey are unchanged. Add
 * new bookings/rota keys HERE, not in en.ts.
 */
import { enEventTypes } from './enEventTypes';

export const enScheduling = {
  ...enEventTypes,
  // ── Booking deposits (E21) ────────────────────────────────────────────
  'branding.title': 'Your booking page',
  'branding.help':
    'For a lot of customers this page is the only one they see before paying. A logo and a colour make it yours.',
  'branding.logo': 'Logo URL',
  'branding.color': 'Accent colour',
  'branding.none': 'Using the neutral default page.',
  'branding.badColor': 'Enter a hex colour like #0f766e, or leave it empty.',
  'branding.badLogo': 'The logo must be an https:// address, or empty.',
  'branding.save': 'Save branding',
  'branding.saving': 'Saving…',
  'branding.saved': 'Booking page branding saved.',
  'branding.saveFailed': 'Could not save the branding.',

  'googleCalendar.title': 'Google Calendar',
  'googleCalendar.help':
    'Connect your Google Calendar and the times you are busy there stop being offered here — and the bookings you take show up in Google.',
  'googleCalendar.connect': 'Connect Google Calendar',
  'googleCalendar.connected': 'Connected — syncing with {calendar}.',
  'googleCalendar.disconnect': 'Disconnect',
  'googleCalendar.disconnecting': 'Disconnecting…',
  'googleCalendar.disconnected': 'Google Calendar disconnected.',
  'googleCalendar.disconnectFailed': 'Could not disconnect Google Calendar.',
  'googleCalendar.notConfigured':
    'Google Calendar is not set up on this installation yet, so there is nothing to connect to. This is on our side, not yours.',
  'googleCalendar.syncError': 'Last sync did not finish: {error}',

  'serviceStats.title': 'How each service is doing',
  'serviceStats.help':
    'The last 30 days, worst no-show rate first — that is usually the one worth taking a deposit on.',
  'serviceStats.service': 'Service',
  'serviceStats.booked': 'Booked',
  'serviceStats.noShowRate': 'No-shows',
  'serviceStats.revenue': 'Earned',
  'serviceStats.unnamed': 'No service named',

  // /calendar page header — pairs with calendar.title in en.ts (Account-style subtitle).
  'calendar.subtitle':
    'Bookings, posts, campaigns, and sends — scheduled in one place.',

  // Insight cards above the calendar grid (Overview / Content Type / Platform).
  'social.calendar.insights': 'Calendar insights',
  'social.calendar.overview': 'Overview',
  'social.calendar.stat.total': 'Total',
  'social.calendar.stat.scheduled': 'Scheduled',
  'social.calendar.stat.published': 'Published',
  'social.calendar.stat.drafts': 'Drafts',
  'social.calendar.contentTypes': 'Content Type',
  'social.calendar.content.post': 'Post',
  'social.calendar.content.reel': 'Reel',
  'social.calendar.content.story': 'Story',
  'social.calendar.content.carousel': 'Carousel',
  'social.calendar.platforms': 'Platform',
  'social.calendar.platformsEmpty': 'No posts on a connected platform yet.',
  'social.calendar.viewGroup': 'Calendar view',
  'social.calendar.loadFailed': 'Could not load calendar events. Check your connection and try again.',
  'social.calendar.retry': 'Retry',

  // Organic numbers on a posted item in the day agenda (post metrics sync).
  'calendar.day.metrics': '{reach} reached · {likes} likes · {comments} comments',
  'calendar.day.metricsNoReach': '{likes} likes · {comments} comments',
  'integrationHealth.title': 'Some connections need attention',
  'integrationHealth.neverSynced':
    'connected, but nothing has come through yet. This is usually a missing permission — try reconnecting it.',
  'integrationHealth.failing': 'is failing to sync. The credential has probably expired — reconnect it.',
  'integrationHealth.needsReauth': 'expired and must be reconnected.',
  'integrationHealth.stale':
    'has not synced in over a day. It still looks connected, so nothing here has been updating.',

  'referrals.title': 'Refer a friend',
  'referrals.help':
    'Give your happiest customers a link to share. When someone they send buys, you credit both of them automatically.',
  'referrals.enable': 'Run a referral programme',
  'referrals.off': 'Off — no referral credits are issued and no links are handed out.',
  'referrals.rewardType': 'Reward',
  'referrals.percentage': 'Percentage off',
  'referrals.fixed': 'Fixed amount off',
  'referrals.referrer': 'The one who shared gets',
  'referrals.referee': 'Their friend gets',
  'referrals.summary':
    'When a referred friend buys, we create two single-use codes: {referrer} off for the customer who shared, and {referee} off for their friend.',
  'referrals.save': 'Save rewards',
  'referrals.saving': 'Saving…',
  'referrals.saved': 'Referral programme saved.',
  'referrals.saveFailed': 'Could not save the referral programme.',
  'referrals.loading': 'Loading referral settings…',

  'dailyCap.title': 'Most bookings a day',
  'dailyCap.help':
    'The most appointments this booking page will take on one day. Once a day is full it stops being offered — nobody is shown a time they cannot have.',
  'dailyCap.perDay': 'Bookings per day',
  'dailyCap.windowDays': 'Bookable days ahead',
  'dailyCap.increment': 'Offer times every (min)',
  'dailyCap.none': 'No limit — this page takes as many bookings a day as your hours allow. Leave "days ahead" empty for the usual two weeks, and "offer times every" empty to step by the appointment length.',
  'dailyCap.save': 'Save limit',
  'dailyCap.saving': 'Saving…',
  'dailyCap.saved': 'Daily limit saved.',
  'dailyCap.saveFailed': 'Could not save the daily limit.',
  'dailyCap.invalid': 'Enter a whole number of bookings, or leave it empty for no limit.',

  'deposit.title': 'Take a deposit to confirm',
  'deposit.help':
    'Customers pay before the booking is confirmed. The slot is held while they pay and released if they do not — the strongest thing you can do about no-shows.',
  'deposit.amount': 'Deposit (₪)',
  'deposit.none': 'No deposit — bookings confirm straight away.',
  'deposit.save': 'Save deposit',
  'deposit.saving': 'Saving…',
  'deposit.saved': 'Deposit saved.',
  'deposit.saveFailed': 'Could not save the deposit.',
  'deposit.notConnected':
    'Connect Cardcom under Settings → Integrations to take deposits. Until then bookings confirm without payment.',
  'booking.depositDue': 'A deposit of ₪{amount} confirms this booking.',
  'booking.payNow': 'Pay the deposit',

  // ── Customer-facing manage page (/booking/:token) ─────────────────────
  'manage.invalidTitle': 'This link is no longer valid',
  'manage.invalidBody':
    'It may have already been cancelled. If you still need to change your appointment, please contact us directly.',
  'manage.cancelledTitle': 'Your booking is cancelled',
  'manage.cancelledBody': 'Thanks for letting us know — the time is now free for someone else.',
  'manage.loading': 'Loading…',
  'manage.fallbackTitle': 'Your booking',
  'manage.duration': '{minutes} minutes',
  'manage.moved': 'Moved. We have emailed you the new details.',
  'manage.join': 'Join the call',
  'manage.pickNewTime': 'Pick a new time',
  'manage.cancel': 'Cancel booking',
  // The confirm step before a cancel — one tap used to cancel outright, and
  // the link then answered "no longer valid" with no way back.
  'manage.cancelConfirmTitle': 'Cancel this booking?',
  'manage.cancelConfirmBody':
    'Your appointment will be cancelled and the time freed for someone else. This link will then stop working — if you change your mind, you will need to book again.',
  'manage.cancelConfirmKeep': 'Keep my booking',
  'manage.cancelConfirmYes': 'Yes, cancel it',
  'manage.cancelling': 'Cancelling…',
  'manage.noOtherTimes': 'No other times are free at the moment.',

  // ── Date overrides (holidays, one-off closures) ───────────────────────
  'closures.title': 'Holidays and one-off closures',
  'closures.help':
    'Days your normal hours are wrong about. Close for a holiday, or open a day you are usually shut — these win over your weekly hours.',
  'closures.closedAllDay': 'Closed all day',
  'closures.notePlaceholder': 'Note (optional)',
  'closures.add': 'Add',
  'closures.saving': 'Saving…',
  'closures.remove': 'Remove',
  'closures.saved': 'Saved — your booking page is updated.',
  'closures.saveFailed': 'Could not save that closure.',
  'closures.deleteFailed': 'Could not remove that closure.',

  // ── Round-robin rota (booking page staff) ─────────────────────────────
  'rota.title': 'Share bookings across your team',
  'rota.help':
    'Pick who takes bookings on this page. Each new booking goes to whoever was assigned least recently and is actually free, so two people can hold the same time. Leave everyone unticked if you work alone.',
  'rota.soloNow': 'Working alone — every booking blocks the whole calendar.',
  'rota.sharing': 'Sharing across {count} people.',
  'rota.noMembers': 'Invite teammates to your workspace to share bookings.',
  'rota.pendingHint':
    'Members who have not accepted their invite yet cannot take bookings.',
  'rota.save': 'Save team',
  'rota.saving': 'Saving…',
  'rota.saved': 'Booking rota saved.',
  'rota.saveFailed': 'Could not save the rota.',

  // ── Bookings tab + week calendar (kind-alignment §6) ──────────────────
  'bookings.window.today': 'Today',
  'bookings.window.upcoming': 'Upcoming',
  'bookings.window.past': 'Past',
  'bookings.view.week': 'Week',
  'bookings.view.list': 'List',
  'bookings.join': 'Join',
  'bookings.empty.today': 'Nothing scheduled today',
  'hub.calendar.emptyTitle': 'Nothing scheduled this month',
  'hub.calendar.emptyBody':
    'Add a bookable service, schedule a social post, or connect your calendar — then this timeline fills in.',
  'hub.calendar.ctaIntegrations': 'Open Integrations',
  'hub.calendar.ctaSocial': 'Schedule a post',
  'bookings.empty.upcoming': 'No upcoming bookings',
  'bookings.empty.past': 'No past bookings yet',
  'bookings.empty.hint':
    'Bookings land here from your booking page, Calendly, or when you create them from the week view.',
  'bookings.week.prev': 'Previous week',
  'bookings.week.next': 'Next week',
  'bookings.new': 'New booking',
  // P7: the kind's own noun — a coaching Hub reads "Sessions this week". Marketing to review wording.
  'bookings.stats.weekNoun': '{Transactions} this week',
  'bookings.stats.confirmed': 'Confirmed',
  'bookings.status.scheduled': 'Confirmed',
  'bookings.status.showed': 'Showed',
  'bookings.status.noShow': 'No-show',
  'bookings.status.cancelled': 'Cancelled',
  'bookings.week.jumpToToday': 'Jump to today',
  'bookings.week.slotLabel': 'Book {day} at {time}',
  'bookings.createError': 'Could not create the booking.',
  'bookings.updateError': 'Could not update the booking.',
  'bookings.sheet.newTitle': 'New booking',
  'bookings.sheet.editTitle': 'Edit booking',
  'bookings.embed.toggle': 'Embed on your website',
  'bookings.embed.hint': 'Paste this where the booking page should appear on your site. It fits its height to the page and works on any website.',
  'bookings.embed.redirectHint': 'Optional: add data-redirect="https://your-site/thanks" to the div to send bookers to your own page after they book.',
  'bookings.embed.copy': 'Copy code',
  'bookings.embed.copied': 'Copied',
  'bookings.overlap': 'That time overlaps another booking. Pick another time.',
  'bookings.sheet.close': 'Close',
  'bookings.sheet.client': 'Client',
  'bookings.sheet.clientPlaceholder': 'Search your clients…',
  'bookings.sheet.change': 'Change',
  'bookings.sheet.service': 'Service',
  'bookings.sheet.servicePick': 'Pick a service (sets the duration)',
  'bookings.sheet.date': 'Date',
  'bookings.sheet.time': 'Time',
  'bookings.sheet.duration': 'Duration (minutes)',
  'bookings.sheet.status': 'Status',
  'bookings.sheet.save': 'Save booking',
  'bookings.sheet.book': 'Book it',
  'bookings.sheet.cancel': 'Cancel',
  'bookings.sheet.cancelBooking': 'Cancel booking',

  // ── The routing form at /r/:slug — one question, then a booking page ──
  'router.loading': 'Loading…',
  'router.gone': 'That link is no longer available.',
  'router.loadFailed': 'We could not load this page. Please try again.',
  'router.nothingBookable': 'There is nothing available to book here right now.',
  'router.minutes': '{n} min',
  'booking.questions.title': 'Ask before the appointment',
  'booking.questions.help': 'Questions the booker answers when they pick a time. Their replies arrive with the booking.',
  'booking.questions.none': 'No questions — bookers just give a name, an email and a time.',
  'booking.questions.newLabel': 'New question',
  'booking.questions.add': 'Add question',
  'booking.questions.remove': 'Remove question',
  'booking.questions.required': 'Required',
  'booking.questions.typeText': 'Text',
  'booking.questions.typeChoice': 'Choice',
  'booking.questions.optionsPlaceholder': 'Options, separated by commas',
  'booking.questions.save': 'Save questions',
  'booking.questions.saving': 'Saving…',
  'booking.questions.saved': 'Questions saved',
  'booking.questions.saveFailed': 'Could not save the questions',
  'booking.questions.selectPlaceholder': 'Select…',

  // ── Public booking page (/book/:slug) ─────────────────────────────────
  'booking.public.timesShownIn': 'Times shown in {zone}',
  'booking.public.timesShownInMine': 'Times shown in your time zone ({zone})',
  'booking.public.showMyZone': 'Show in my time zone ({zone})',
  'booking.public.showMerchantZone': 'Show in {zone}',
  'booking.public.phone': 'Phone (optional)',
  'booking.public.phonePlaceholder': '+972 50 123 4567',
  'booking.public.phoneHint': 'For the confirmation and reminders on WhatsApp or SMS.',
  'booking.public.manage': 'Need to change it? Reschedule or cancel',

  // ── Agent canvas booking nodes (B17) ──────────────────────────────────
  'canvas.booking.eventType': 'Event type',
  'canvas.booking.defaultEventType': 'Workspace default',
  'canvas.booking.linkText': 'Message',
  'canvas.booking.linkHint': 'Use {{bookingPageUrl}} to place the link; otherwise it is added at the end.',
  'canvas.booking.textHe': 'Hebrew message (optional)',
  'canvas.booking.textEn': 'English message (optional)',
  'canvas.booking.promptText': 'Question',
  'canvas.booking.promptHint': 'Use {{slots}} to place the list of times. The time zone line is always added.',
  'canvas.booking.slotCount': 'How many times to offer',
  'canvas.booking.offerHint': 'Put a "Book slot" step right after this one — it reads the reply.',
  'canvas.booking.confirmText': 'Confirmation',
  'canvas.booking.confirmHint': 'Use {{time}}, {{service}}, {{joinUrl}} and {{manageUrl}}.',
  'canvas.booking.takenText': 'If the time was just taken',
  'canvas.booking.branchHint': 'Two branches: booked → left, unavailable → right.',
  'canvas.booking.assistantHint': 'Books, moves and cancels by DM in Hebrew and English, and asks the customer to confirm before any cancel or move. Connect one step after it for questions that are not about booking.',

  // ── Workspace time zone (Settings → Profile) ──────────────────────────
  'settings.timezone.section': 'Time zone',
  'settings.timezone.label': 'Workspace time zone',
  'settings.timezone.help':
    'Your booking hours, confirmations and reminders all use this clock. Customers see times in it.',
  'settings.timezone.placeholder': 'Choose a time zone…',
  'settings.timezone.saved': 'Time zone updated.',
  'settings.timezone.saveFailed': 'Could not update the time zone. Please try again.',
  'settings.timezone.noWorkspace': 'No workspace is selected. Reload the page and try again.',

  // ── Account → Profile Information ─────────────────────────────────────
  'settings.profile.title': 'Profile Information',
  'settings.profile.fullName': 'Full Name',
  'settings.profile.email': 'Email Address',
  'settings.profile.emailReadonly': 'Email address cannot be changed here',
  'settings.profile.uploadPhoto': 'Upload Photo',
  'settings.profile.replacePhoto': 'Replace Photo',
  'settings.profile.photoHint': 'PNG, JPG, or JPEG · Max 5 MB',
  'settings.profile.photoProcessing': 'Processing photo…',
  'settings.profile.save': 'Save Changes',
  'settings.profile.saving': 'Saving…',
  'settings.profile.nameRequired': 'Full name is required',
  'settings.profile.nameTooShort': 'Full name must be at least 2 characters',
  'settings.profile.nameTooLong': 'Full name must be at most 80 characters',
  'settings.profile.loadFailed': 'Could not load your profile. Please try again.',
  'settings.profile.retry': 'Retry',
  'settings.profile.savedToast': 'Profile saved',
  'settings.profile.saveFailedToast': 'Failed to save — please try again',

  'settings.account.title': 'Account',
  'settings.account.subtitle':
    'Profile, brand, billing, and integrations for this workspace.',
  'settings.account.back': 'Account',
  'settings.account.tab.profile': 'Profile',
  'settings.account.tab.brand': 'Brand DNA',
  'settings.account.tab.billing': 'Billing',
  'settings.account.tab.integrations': 'Integrations',
  'settings.account.tab.support': 'Support',
  'settings.account.section.businessKind': 'Business kind',
  'settings.account.section.profile': 'Profile Information',
  'settings.account.section.security': 'Security',
  'settings.account.section.notifications': 'Notifications',
  'settings.account.section.widgets': 'Phone & Desktop Widgets',
  'settings.account.section.general': 'General',
  'settings.account.section.brandIdentity': 'Brand Identity',
  'settings.account.section.team': 'Team',
  'settings.account.section.email': 'Email',
  'settings.account.tabsAria': 'Account sections',

  'settings.notifications.title': 'Push Notifications',
  'settings.notifications.mobilePush': 'Mobile Push',
  'settings.notifications.mobilePushHint':
    'New orders, completed flows, and important account events on your devices',
  'settings.notifications.savedToast': 'Notification preference saved',
  'settings.notifications.saveFailedToast': 'Could not update notifications — please try again',

  'settings.security.sessionsLoading': 'Loading sessions…',
  // V54: a new workspace's booking page opens on these default hours.
  'bookings.defaultHours.nudge': 'Your booking page is open Mon–Fri, 09:00–17:00 in your time zone — default hours until you set your own.',
  'bookings.defaultHours.edit': 'Edit hours',
  // BuzzCall, before the slot: the room opens ten minutes before the call.
  'call.waiting.on': 'Your call is on',
  'call.waiting.opens': 'The room opens 10 minutes before the call. Keep this link; nothing else is needed.',
  'call.waiting.opensIn': 'Opens in',
} as const;

export { enComposer } from './enComposer';
